import canadaWeatherData from "data/canadaWeatherData.json";

const PROVINCE_NAMES = {
    BC: "British Columbia",
    AB: "Alberta",
    SK: "Saskatchewan",
    MB: "Manitoba",
    ON: "Ontario",
    QC: "Quebec",
    NB: "New Brunswick",
    PE: "Prince Edward Island",
    NS: "Nova Scotia",
    NL: "Newfoundland and Labrador",
    YT: "Yukon",
    NT: "Northwest Territories",
    NU: "Nunavut",
};

/** Compact JSON value for <select> (unique per row). */
export function rowToSelectValue(row) {
    return JSON.stringify({
        c: row.city,
        r: row.region,
        la: row.latitude,
        lo: row.longitude,
        hdd: row.hdd,
        dht: row.designHeatingTemp,
        cdt: row.designCoolingTemp,
    });
}

export function parseLocationSelectValue(value) {
    if (!value) return null;
    try {
        const o = JSON.parse(value);
        if (typeof o.c !== "string" || typeof o.r !== "string") {
            return null;
        }
        const la = typeof o.la === "number" ? o.la : parseFloat(o.la);
        const lo = typeof o.lo === "number" ? o.lo : parseFloat(o.lo);
        const hdd = typeof o.hdd === "number" ? o.hdd : parseFloat(o.hdd);
        const dht = typeof o.dht === "number" ? o.dht : parseFloat(o.dht);
        const cdt = typeof o.cdt === "number" ? o.cdt : parseFloat(o.cdt);
        if (Number.isNaN(la) || Number.isNaN(lo)) {
            return null;
        }

        return {
            city: o.c,
            region: o.r,
            latitude: la,
            longitude: lo,
            hdd: hdd,
            designHeatingTemp: dht,
            designCoolingTemp: cdt,
        };
    } catch {
        return null;
    }
}

export function getWeatherRowById(id) {
    if (!id) return null;
    const row = canadaWeatherData[id];
    return row ? { id, ...row } : null;
}

export function getLocationStateFromRowId(id) {
    const row = getWeatherRowById(id);
    if (!row) {
        return {
            location_city_id: "",
            location: "",
            normalized_location_name: "",
            location_region: "",
            location_latitude: null,
            location_longitude: null,
            location_select_value: "",
        };
    }

    return {
        location_city_id: id,
        location: id,
        normalized_location_name: `${row.city}, ${row.region}`,
        location_region: row.region,
        location_latitude: row.latitude,
        location_longitude: row.longitude,
        location_select_value: rowToSelectValue(row),
    };
}

const sortedRegions = [...new Set(Object.values(canadaWeatherData).map((row) => row.region))].sort((a, b) =>
    PROVINCE_NAMES[a].localeCompare(PROVINCE_NAMES[b]),
);

export const CANADA_WEATHER_REGION_SELECT_OPTIONS = [
    { value: "", label: "Select a region" },
    ...sortedRegions.map((region) => ({
        value: region,
        label: PROVINCE_NAMES[region] || region,
    })),
];

export function getCitySelectOptionsForRegion(region) {
    if (!region) {
        return [{ value: "", label: "Select a region first" }];
    }

    const cities = Object.entries(canadaWeatherData)
        .filter(([, row]) => row.region === region)
        .map(([id, row]) => ({ id, city: row.city }))
        .sort((a, b) => a.city.localeCompare(b.city));

    return [{ value: "", label: "Select a city" }, ...cities.map(({ id, city }) => ({ value: id, label: city }))];
}

export const CANADA_WEATHER_LOCATION_SELECT_OPTIONS = [
    { value: "", label: "Select closest station reference" },
    ...Object.values(canadaWeatherData).map((row) => ({
        value: rowToSelectValue(row),
        label: `${row.city} (${row.region})`,
    })),
];

function findDefaultLocationRow() {
    const torontoOn = Object.entries(canadaWeatherData).find(
        ([, row]) => row.region === "ON" && row.city.toLowerCase().includes("toronto"),
    );
    if (torontoOn) return { id: torontoOn[0], ...torontoOn[1] };
    const firstEntry = Object.entries(canadaWeatherData)[0];
    return firstEntry ? { id: firstEntry[0], ...firstEntry[1] } : null;
}

/** Human-readable label for a weather data row id (e.g. 490 → Toronto (city hall), ON). */
export function labelForBackendLocationKey(key) {
    if (!key || typeof key !== "string") return "";
    const rowById = canadaWeatherData[key];
    if (rowById) return `${rowById.city}, ${rowById.region}`;
    const row = Object.values(canadaWeatherData).find((r) => r.backendLocationKey === key);
    return row ? `${row.city}, ${row.region}` : "";
}

export function getInitialLocationState() {
    const row = findDefaultLocationRow();
    if (!row) {
        return {
            location: "",
            normalized_location_name: "",
            location_region: "",
            location_city_id: "",
            location_latitude: null,
            location_longitude: null,
            location_select_value: "",
        };
    }
    return getLocationStateFromRowId(row.id);
}
